import { useRef, useMemo, Suspense } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { useGLTF, Environment, ContactShadows } from '@react-three/drei'

// Rotating nail model
function NailOnPodium() {
  const groupRef = useRef()
  const nailRef = useRef()
  const { scene: nailScene } = useGLTF('/NailCircuit.glb')
  const { scene: podiumScene } = useGLTF('/Podium.glb')

  // Clone scenes
  const clonedNail = useMemo(() => {
    const clone = nailScene.clone()
    clone.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true
        child.receiveShadow = true
        if (child.material) {
          child.material.envMapIntensity = 1.2
          child.material.needsUpdate = true
        }
      }
    })
    return clone
  }, [nailScene])

  const clonedPodium = useMemo(() => {
    const clone = podiumScene.clone()
    clone.traverse((child) => {
      if (child.isMesh) {
        child.receiveShadow = true
        if (child.material) {
          // Make podium subtle and elegant
          child.material.color.set('#2A2A2A')
          child.material.metalness = 0.3
          child.material.roughness = 0.7
          child.material.needsUpdate = true
        }
      }
    })
    return clone
  }, [podiumScene])

  // Continuous rotation animation
  useFrame((state) => {
    if (groupRef.current) {
      // Slow, elegant rotation
      groupRef.current.rotation.y += 0.003
    }
    if (nailRef.current) {
      // Subtle floating effect
      nailRef.current.position.y = 0.8 + Math.sin(state.clock.elapsedTime * 0.5) * 0.05
    }
  })

  return (
    <group ref={groupRef}>
      {/* Nail */}
      <group ref={nailRef} position={[0, 0.8, 0]} scale={2}>
        <primitive object={clonedNail} rotation={[Math.PI, 0, 0]} />
      </group>

      {/* Podium */}
      <group position={[0, -1.2, 0]} scale={2.5}>
        <primitive object={clonedPodium} />
      </group>
    </group>
  )
}

// Lighting setup for clean, professional look
function Lighting() {
  return (
    <>
      {/* Ambient - soft fill */}
      <ambientLight intensity={0.4} />

      {/* Key light - main illumination */}
      <directionalLight
        position={[5, 5, 5]}
        intensity={0.8}
        color="#FFFFFF"
        castShadow
        shadow-mapSize={[1024, 1024]}
      />

      {/* Fill light - soften shadows */}
      <directionalLight
        position={[-5, 3, -5]}
        intensity={0.3}
        color="#F5F5F5"
      />

      {/* Rim light - edge definition */}
      <pointLight
        position={[0, 5, -5]}
        intensity={0.4}
        color="#FFFFFF"
      />
    </>
  )
}

// Loading fallback
function Loader() {
  return (
    <mesh>
      <boxGeometry args={[0.5, 0.5, 0.5]} />
      <meshStandardMaterial color="#E5E5E5" />
    </mesh>
  )
}

export default function SimpleNailScene({ className = '' }) {
  return (
    <div className={`w-full h-full ${className}`}>
      <Canvas
        camera={{ position: [0, 1, 6], fov: 35 }}
        shadows
        dpr={[1, 2]}
        gl={{ antialias: true, alpha: true }}
        style={{ background: 'transparent' }}
      >
        <Suspense fallback={<Loader />}>
          <Lighting />
          <NailOnPodium />

          {/* Environment for reflections */}
          <Environment preset="studio" />

          {/* Contact shadow for grounding */}
          <ContactShadows
            position={[0, -1.2, 0]}
            opacity={0.3}
            scale={10}
            blur={2}
            far={4}
          />
        </Suspense>
      </Canvas>
    </div>
  )
}

// Preload models
useGLTF.preload('/NailCircuit.glb')
useGLTF.preload('/Podium.glb')
